import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { ThemesHttpRepository } from "../repository";
import { useThemesFilterStore } from "../stores";
import { useDebounce } from "@/hooks/useDebounce";

const PAGE_SIZE = 10;
const themesRepository = new ThemesHttpRepository();

export function useThemesList() {
  const [page, setPage] = useState(1);
  const searchValue = useThemesFilterStore((s) => s.searchValue);
  const statusFilter = useThemesFilterStore((s) => s.statusFilter);
  const ordering = useThemesFilterStore((s) => s.ordering);
  const debouncedSearch = useDebounce(searchValue, 500);

  const [prevSearch, setPrevSearch] = useState(debouncedSearch);
  if (debouncedSearch !== prevSearch) {
    setPrevSearch(debouncedSearch);
    setPage(1);
  }

  const [prevStatusFilter, setPrevStatusFilter] = useState(statusFilter);
  if (statusFilter !== prevStatusFilter) {
    setPrevStatusFilter(statusFilter);
    setPage(1);
  }

  const [prevOrdering, setPrevOrdering] = useState(ordering);
  if (ordering !== prevOrdering) {
    setPrevOrdering(ordering);
    setPage(1);
  }

  const query = useQuery({
    queryKey: [
      "themes",
      { page, search: debouncedSearch, status: statusFilter, ordering },
    ],
    queryFn: () =>
      themesRepository.fetchList({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: (statusFilter as "active" | "inactive") ?? undefined,
        ordering: ordering ?? undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const themes = query.data?.results ?? [];
  const pageCount = query.data ? Math.ceil(query.data.count / PAGE_SIZE) : 0;

  return {
    themes,
    pageCount,
    page,
    setPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
  };
}
